/**
 * Partner Admin API Service
 *
 * Direct API implementation for Partner Admin panel.
 * Provides authentication and data access for partner accountant offices.
 */

import axios from 'axios'
import type { AxiosInstance } from 'axios'

// API Base URL - use environment variable or default
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

// Storage keys
const TOKEN_KEY = 'ngc_partner-admin_token'
const REFRESH_TOKEN_KEY = 'ngc_partner-admin_refresh_token'
const USER_KEY = 'ngc_partner-admin_user'
const PARTNER_KEY = 'ngc_partner-admin_partner'

// Types
export interface PartnerUser {
  id: string
  email: string
  name: string
  role: string
  partnerId?: string
  permissions?: string[]
}

// Notification Settings Types
export interface NotificationSettings {
  email_notifications: boolean
  push_notifications: boolean
  sms_notifications: boolean
  notifications: {
    tax_deadlines: boolean
    document_uploads: boolean
    banking_updates: boolean
    system_alerts: boolean
    marketing: boolean
  }
  digest_frequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'never'
  quiet_hours?: {
    enabled?: boolean
    start?: string
    end?: string
  }
}

export interface Partner {
  id: string
  name: string
  slug: string
  primaryColor?: string
  secondaryColor?: string
  logoUrl?: string
}

export interface MfaSetupResponse {
  qr_code: string
  secret_key: string
  instructions: string
}

export interface AuthResponse {
  access_token: string
  refresh_token?: string
  user: PartnerUser
  partner: Partner
}

export interface MfaChallenge {
  mfa_required: true
  temp_token: string
  user: PartnerUser
  partner: Partner
}

export interface PartnerConfig {
  name: string
  slug: string
  primaryColor: string
  secondaryColor: string
  logoUrl?: string
  companyName: string
}

export interface Client {
  id: string
  name: string
  slug: string
  status: string
  subscriptionPlan: string
  monthlyRevenue: number
  lastActivity: string
  joinedDate: string
  healthScore: number
  pendingTasks: number
  totalUsers: number
  location: string
  contact: {
    email: string
    phone: string
  }
}

export interface ClientsResponse {
  clients: Client[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

// CNPJ Validation Types
export interface CNPJValidationResult {
  valid: boolean
  cnpj: string
  formatted: string
  message: string
}

export interface CNPJLookupResult {
  success: boolean
  cnpj: string
  razaoSocial?: string
  nomeFantasia?: string
  situacaoCadastral?: string
  dataAbertura?: string
  endereco?: {
    logradouro: string
    numero: string
    complemento?: string
    bairro: string
    municipio: string
    uf: string
    cep: string
  }
  atividadePrincipal?: {
    codigo: string
    descricao: string
  }
  atividadesSecundarias?: Array<{
    codigo: string
    descricao: string
  }>
  naturezaJuridica?: string
  capitalSocial?: number
  porte?: string
  email?: string
  telefone?: string
  message?: string
}

export interface CNPJFullValidationResult {
  valid: boolean
  cnpj: string
  formatted: string
  formatValid: boolean
  rfValid: boolean
  isMedicalPractice: boolean
  isActive: boolean
  rfData?: CNPJLookupResult
  errors: string[]
  warnings: string[]
}

export interface CNPJExistsResult {
  exists: boolean
  client: {
    id: string
    name: string
    cnpj: string
    status: string
  } | null
}

export interface ClientDetails {
  client: Client & {
    contact: {
      email: string
      phone: string
      address?: string
    }
    settings?: Record<string, any>
  }
  users: Array<{
    id: string
    name: string
    email: string
    role: string
    lastLogin: string
    status: string
  }>
  metrics: {
    totalTransactions: number
    monthlyRevenue: number
    activePatients: number
    completedAppointments: number
    pendingTasks: number
    systemUsage: number
  }
  billingHistory: Array<{
    month: string
    amount: number
    status: string
    dueDate: string
    paymentDate: string | null
  }>
  recentActivity: Array<{
    id: string
    type: string
    description: string
    timestamp: string
    user: string
  }>
}

export interface DashboardData {
  partner: {
    id: string
    name: string
    slug: string
    status: string
  }
  metrics: {
    totalClients: number
    activeClients: number
    pendingTasks: number
    monthlyRevenue: number
    revenueGrowth: number
    complianceRate: number
  }
  lastUpdated: string
}

export interface PartnerProfile {
  id: string
  name: string
  slug: string
  active: boolean
  contactEmail: string
  contactPhone: string
  brandingConfig: Record<string, any>
  featureFlags: Record<string, any>
  commissionPercentage: number
  adminNotes?: string
  createdAt: string
  updatedAt: string
}

export interface Accountant {
  id: string
  name: string
  email: string
  phone?: string
  role: string
  isActive: boolean
  clientCount: number
  lastLogin?: string
  createdAt: string
}

export interface Assignment {
  id: string
  accountant: {
    id: string
    name: string
    email: string
  }
  client: {
    id: string
    name: string
    email: string
  }
  assignedAt: string
  notes?: string
}

// Tax Management Types
export interface TaxObligation {
  id: string
  clientId: string
  clientName: string
  type: 'DAS' | 'DARF' | 'IRPF' | 'ISS' | 'GPS'
  description: string
  dueDate: string
  amount: number
  status: 'pending' | 'paid' | 'overdue' | 'calculated'
  competency: string
  calculatedAt?: string
  paidAt?: string
}

export interface TaxOverview {
  period: string
  overview: {
    totalClients: number
    totalObligations: number
    totalAmount: number
    paidObligations: number
    pendingObligations: number
    overdueObligations: number
    complianceRate: string
  }
  breakdown: {
    byTaxType: {
      [key: string]: {
        count: number
        amount: number
      }
    }
    byClient: any[]
    byStatus: {
      paid: number
      pending: number
      overdue: number
    }
    byDueDate: {
      thisWeek: number
      nextWeek: number
      thisMonth: number
      nextMonth: number
      future: number
    }
  }
  alerts: {
    type: 'critical' | 'warning' | 'info'
    title: string
    description: string
    action: string
    affectedClients: number
  }[]
  generatedAt: string
}

export interface TaxCalendarItem {
  id: string
  date: string
  dueDate: string
  taxType: string
  description: string
  amount: number
  clientCount?: number
  clientId: string
  clientName: string
  status: 'pending' | 'paid' | 'overdue'
  priority: 'high' | 'medium' | 'low'
}

export interface TaxCalendarResponse {
  month: number
  year: number
  calendar: TaxCalendarItem[]
  summary: {
    totalObligations: number
    totalAmount: number
    criticalDates: number
    completedObligations: number
  }
  upcomingDeadlines: TaxCalendarItem[]
}

export interface ClientTaxDetails {
  client: {
    id: string
    name: string
    taxRegime: string
  }
  obligations: TaxObligation[]
  summary: {
    totalDue: number
    totalPaid: number
    totalPending: number
    complianceRate: number
  }
}

export interface TaxCalculationResult {
  clientId: string
  type: string
  revenue: number
  aliquot: number
  deductions: number
  calculatedAmount: number
  period: string
  calculatedAt: string
}

export interface ComplianceReport {
  reportId: string
  period: any
  partner: {
    id: string
    totalClients: number
  }
  complianceMetrics: {
    totalClients: number
    compliantClients: number
    nonCompliantClients: number
    complianceRate: string
    averageComplianceScore: string
    totalObligations: number
    fulfilledObligations: number
  }
  clientDetails: any[]
  riskAnalysis: {
    highRisk: number
    mediumRisk: number
    lowRisk: number
    mainRiskFactors: string[]
  }
  recommendations: string[]
  actionItems: {
    item: string
    priority: 'high' | 'medium' | 'low'
    deadline: string
  }[]
  generatedAt: string
}

// Document Management Types
export interface Document {
  id: string
  name: string
  type: 'invoice' | 'tax_document' | 'medical_record' | 'contract' | 'report'
  status: 'processed' | 'processing' | 'pending' | 'failed'
  clientId: string
  clientName: string
  uploadedAt: string
  processedAt: string | null
  size: number
  category: string
  tags: string[]
  extractedData: {
    documentType: string
    amount: number
    date: string
    entities: string[]
    confidence: string
  } | null
}

export interface DocumentsResponse {
  documents: Document[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  summary: {
    total: number
    byStatus: {
      processed: number
      processing: number
      pending: number
      failed: number
    }
    byType: {
      invoice: number
      tax_document: number
      medical_record: number
      contract: number
      report: number
    }
    totalSize: number
  }
}

export interface ProcessingStatus {
  overview: {
    totalDocuments: number
    pendingProcessing: number
    processing: number
    completed: number
    failed: number
  }
  byClient: {
    clientId: string
    clientName: string
    pending: number
    processing: number
    completed: number
    lastActivity: string
  }[]
  recentActivity: {
    id: string
    type: string
    document: string
    client: string
    status: 'success' | 'failed'
    timestamp: string
  }[]
  alerts: {
    type: 'warning' | 'info' | 'error'
    message: string
    count?: number
    action: string
  }[]
}

export interface DocumentTemplate {
  id: string
  name: string
  category: string
  description: string
  usageCount: number
  recentlyUsed: boolean
  lastModified: string
}

export interface TemplatesResponse {
  templates: DocumentTemplate[]
  categories: {
    id: string
    name: string
    count: number
  }[]
  recentlyUsed: DocumentTemplate[]
  popular: DocumentTemplate[]
}

export interface DocumentAnalytics {
  timeRange: string
  volumeMetrics: {
    totalUploaded: number
    totalProcessed: number
    averagePerDay: number
    peakDay: string
  }
  processingMetrics: {
    averageTime: number
    successRate: string
    throughput: number
    bottlenecks: string[]
  }
  complianceMetrics: {
    complianceRate: string
    criticalIssues: number
    pendingReview: number
  }
  clientMetrics: {
    activeClients: number
    topUploader: string
    mostCompliant: string
  }
  trends: {
    uploadTrend: { date: string; uploads: number; processed: number }[]
    processingTimeTrend: { date: string; avgTime: number }[]
    errorRateTrend: { date: string; errorRate: string }[]
  }
  generatedAt: string
}

// Support N1 Types
export type SupportTicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
export type SupportTicketPriority = 'low' | 'medium' | 'high'
export type SupportTicketCategory = 'technical' | 'billing' | 'training' | 'general'

export interface SupportTicket {
  id: string
  title: string
  status: SupportTicketStatus
  priority: SupportTicketPriority
  category: SupportTicketCategory
  clientName: string
  clientId: string
  assignedTo: string | null
  createdAt: string
  lastUpdated: string
  responseTime: number
  estimatedResolution: string
}

export interface SupportTicketDetails {
  ticket: {
    id: string
    title: string
    description: string
    status: SupportTicketStatus
    priority: SupportTicketPriority
    category: SupportTicketCategory
    clientName: string
    clientId: string
    assignedTo: string
    createdBy: string
    createdAt: string
    lastUpdated: string
    estimatedResolution: string
    tags: string[]
    attachments: {
      name: string
      size: string
      uploadedAt: string
    }[]
  }
  timeline: {
    id: string
    type: 'created' | 'assigned' | 'response' | 'update' | 'status_change'
    message: string
    author: string
    timestamp: string
  }[]
  relatedTickets: {
    id: string
    title: string
    status: SupportTicketStatus
    similarity: number
  }[]
  suggestedActions: {
    action: string
    title: string
    description: string
    priority: SupportTicketPriority
  }[]
}

export interface SupportTicketsResponse {
  tickets: SupportTicket[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  summary: {
    total: number
    open: number
    inProgress: number
    resolved: number
    high: number
    medium: number
    low: number
  }
}

export interface SupportMetrics {
  period: string
  overview: {
    totalTickets: number
    resolvedTickets: number
    avgResolutionTime: number
    satisfactionScore: string
    firstResponseTime: number
  }
  breakdown: {
    byPriority: {
      high: number
      medium: number
      low: number
    }
    byCategory: {
      technical: number
      billing: number
      training: number
      general: number
    }
    byClient: {
      clientName: string
      ticketCount: number
    }[]
    byStatus: {
      open: number
      in_progress: number
      resolved: number
    }
  }
  trends: {
    ticketVolume: { date: string; tickets: number; resolved: number }[]
    resolutionTimes: { date: string; avgTime: number }[]
    satisfactionTrend: { date: string; score: string }[]
  }
  generatedAt: string
}

export interface KnowledgeBaseArticle {
  id: string
  title: string
  summary: string
  category: string
  featured: boolean
  views: number
  helpful: number
  updatedAt: string
  content?: string
}

export interface KnowledgeBaseResponse {
  articles: KnowledgeBaseArticle[]
  categories: {
    id: string
    name: string
    count: number
  }[]
  featuredArticles: KnowledgeBaseArticle[]
  recentlyUpdated: KnowledgeBaseArticle[]
}

export interface SystemStatus {
  overall: 'operational' | 'degraded' | 'outage'
  lastUpdated: string
  services: {
    name: string
    status: 'operational' | 'degraded' | 'outage'
    uptime: number
  }[]
  incidents: any[]
  scheduledMaintenance: any[]
}

export interface QuickAction {
  id: string
  label: string
  description: string
  icon: string
  type: 'primary' | 'secondary'
}

// Analytics Types
export interface AnalyticsOverview {
  period: string
  dateRange: {
    start: string
    end: string
  }
  summary: {
    revenue: {
      total: number
      growth: number
      avgPerClient: number
    }
    clients: {
      total: number
      active: number
      churnRate: number
    }
    operations: {
      healthScore: number
      uptime: number
      documentsProcessed: number
    }
    support: {
      ticketsCreated: number
      ticketsResolved: number
      satisfactionScore: number
    }
  }
  highlights: {
    type: 'positive' | 'warning' | 'negative'
    icon: string
    title: string
    description: string
  }[]
  generatedAt: string
}

export interface FinancialAnalytics {
  reportId: string
  type: string
  period: {
    startDate: string
    endDate: string
    duration: string
  }
  partner: {
    id: string
    totalClients: number
    activeClients: number
  }
  summary: {
    totalRevenue: number
    totalExpenses: number
    netProfit: number
    profitMargin: string
    averageRevenuePerClient: number
    growthRate: string
  }
  breakdown: {
    byClient: {
      id: string
      name: string
      revenue: number
      expenses: number
      profit: number
      profitMargin: string
      transactionCount: number
    }[]
    byMonth: {
      month: string
      revenue: number
      expenses: number
      clientCount: number
    }[]
    byService: {
      service: string
      revenue: number
      clientsUsing: number
      avgTicket: number
    }[]
    expenses: {
      category: string
      amount: number
      percentage: number
    }[]
  }
  trends: {
    revenueChart: {
      date: string
      revenue: number
      expenses: number
    }[]
    clientGrowth: {
      month: string
      totalClients: number
      newClients: number
      churnedClients: number
    }[]
    profitabilityTrend: {
      month: string
      revenue: number
      expenses: number
      profitMargin: string
    }[]
  }
  generatedAt: string
}

export interface RevenueAnalysis {
  period: string
  dateRange: {
    start: string
    end: string
  }
  totals: {
    revenue: number
    expenses: number
    netProfit: number
    profitMargin: string
  }
  breakdown: {
    byClient?: FinancialAnalytics['breakdown']['byClient']
    byMonth?: FinancialAnalytics['breakdown']['byMonth']
    byService?: FinancialAnalytics['breakdown']['byService']
  }
  trends: FinancialAnalytics['trends']
  generatedAt: string
}

export interface OperationalMetrics {
  reportId: string
  type: string
  period: string
  partner: {
    id: string
    totalClients: number
  }
  systemHealth: {
    overallScore: number
    uptime: string
    avgResponseTime: number
    errorRate: string
  }
  clientActivity: {
    totalLogins: number
    avgSessionDuration: number
    documentsProcessed: number
    reportsGenerated: number
  }
  support: {
    ticketsCreated: number
    ticketsResolved: number
    avgResolutionTime: number
    satisfactionScore: string
  }
  productivity: {
    tasksCompleted: number
    automationRate: number
    processingTime: number
  }
  clientBreakdown: {
    id: string
    name: string
    status: string
    lastActivity: string
    healthScore: number
    monthlyUsage: {
      logins: number
      documentsUploaded: number
      reportsGenerated: number
      hoursActive: number
    }
  }[]
  generatedAt: string
}

export interface ClientPerformance {
  period: string
  totalClients: number
  clients: OperationalMetrics['clientBreakdown']
  summary: {
    avgHealthScore: number
    activeClients: number
    clientsNeedingAttention: number
  }
  generatedAt: string
}

export interface ClientComparison {
  reportId: string
  type: string
  clients: {
    client: {
      id: string
      name: string
      status: string
      joinedDate: string
    }
    metrics: Record<string, number>
  }[]
  selectedMetrics: string[]
  benchmarks: Record<string, {
    industryAverage: number
    topPerformer: number
    minimum: number
  }>
  insights: string[]
  generatedAt: string
}

export interface GrowthAnalytics {
  period: string
  dateRange: {
    start: string
    end: string
  }
  metrics: {
    revenueGrowth: number
    clientGrowthRate: number
    profitGrowthRate: number
  }
  trends: {
    clientGrowth: {
      month: string
      totalClients: number
      newClients: number
      churnedClients: number
    }[]
    revenueGrowth: {
      month: string
      revenue: number
      expenses: number
      profitMargin: string
    }[]
    profitability: {
      month: string
      revenue: number
      expenses: number
      profitMargin: string
    }[]
  }
  projections: {
    next3Months: number
    next6Months: number
    next12Months: number
    confidence: string
    assumptions: string[]
  }
  insights: string[]
  generatedAt: string
}

export interface ChurnAnalytics {
  period: string
  metrics: {
    churnRate: string
    atRiskCount: number
    retentionRate: string
    avgLifetime: number
  }
  atRiskClients: {
    id: string
    name: string
    healthScore: number
    lastActivity: string
    riskFactors: string[]
    recommendedActions: string[]
  }[]
  churnReasons: {
    reason: string
    percentage: number
  }[]
  retentionTrend: {
    month: string
    retentionRate: string
    churnedClients: number
    newClients: number
  }[]
  generatedAt: string
}

export interface LTVAnalytics {
  metrics: {
    ltv: number
    arpu: number
    avgLifetimeMonths: number
    cac: number
    ltvCacRatio: string
  }
  segmentation: {
    byPlan: {
      plan: string
      ltv: number
      arpu: number
      clientCount: number
    }[]
    byTenure: {
      tenure: string
      ltv: number
      avgMonths: number
    }[]
  }
  trends: {
    month: string
    ltv: number
    arpu: number
  }[]
  generatedAt: string
}

export interface ReportTemplate {
  id: string
  name: string
  description: string
  category: string
  requiredParams: string[]
  outputFormats: string[]
}

export interface ReportTemplatesResponse {
  templates: ReportTemplate[]
  categories: {
    id: string
    name: string
    icon: string
  }[]
}

export interface ExportResult {
  export: {
    reportId: string
    format: string
    downloadUrl: string
    fileSize: number
    generatedAt: string
    expiresAt: string
  }
  message: string
  instructions: string
}

/**
 * Create axios instance with proper configuration
 */
function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  // Request interceptor - add auth token
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor - handle errors
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // If refresh itself fails, clear auth and redirect
      if (originalRequest?.url?.includes('/auth/refresh')) {
        clearAuthData()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      // If auth error and not already retrying
      if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
        originalRequest._retry = true

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        const partnerSlug = getStoredPartnerSlug()

        if (refreshToken && partnerSlug) {
          try {
            // Try to refresh the token
            const response = await instance.post(`/partner-admin/${partnerSlug}/auth/refresh`, {
              refresh_token: refreshToken
            })

            const { access_token, refresh_token: newRefreshToken } = response.data

            // Update tokens
            localStorage.setItem(TOKEN_KEY, access_token)
            if (newRefreshToken) {
              localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)
            }

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${access_token}`
            return instance(originalRequest)
          } catch (refreshError) {
            // Refresh failed - clear auth and redirect
            clearAuthData()
            window.location.href = '/login'
            return Promise.reject(refreshError)
          }
        } else {
          // No refresh token - clear auth and redirect
          clearAuthData()
          window.location.href = '/login'
        }
      }
      return Promise.reject(error)
    }
  )

  return instance
}

// Helper functions
function clearAuthData() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem(PARTNER_KEY)
  localStorage.removeItem('ngc_partner-admin_is_authenticated')
}

function getStoredPartnerSlug(): string {
  try {
    const partnerData = localStorage.getItem(PARTNER_KEY)
    if (partnerData) {
      const partner = JSON.parse(partnerData)
      return partner.slug
    }
  } catch (e) {
    console.error('Error getting partner slug:', e)
  }
  return 'silva-contadores' // Default for development
}

export function getPartnerSlug(): string {
  // Check localStorage first (from previous login)
  const storedSlug = getStoredPartnerSlug()
  if (storedSlug && storedSlug !== 'silva-contadores') {
    return storedSlug
  }

  // Check URL for whitelabel domains (not Cloudflare Pages)
  const hostname = window.location.hostname
  if (hostname !== 'localhost' &&
      !hostname.includes('127.0.0.1') &&
      !hostname.includes('pages.dev')) {
    // Extract subdomain or use domain mapping
    const parts = hostname.split('.')
    if (parts.length > 2) {
      return parts[0]
    }
  }

  // Default partner slug
  return 'silva-contadores'
}

export const api = createApiClient()

/**
 * Partner Admin API endpoints
 */
export const partnerAdminApi = {
  // Authentication
  auth: {
    login: async (partnerSlug: string, email: string, password: string): Promise<AuthResponse | MfaChallenge> => {
      const response = await api.post(`/partner-admin/${partnerSlug}/auth/login`, {
        email,
        password
      })
      return response.data
    },

    verifyMfa: async (partnerSlug: string, tempToken: string, code: string): Promise<AuthResponse> => {
      const response = await api.post(`/partner-admin/${partnerSlug}/auth/mfa/verify`, {
        tempToken,
        code
      })
      return response.data
    },

    logout: async (partnerSlug: string): Promise<void> => {
      await api.post(`/partner-admin/${partnerSlug}/auth/logout`)
    },

    getCurrentUser: async (partnerSlug: string): Promise<{ user: PartnerUser; partner: Partner }> => {
      const response = await api.get(`/partner-admin/${partnerSlug}/auth/me`)
      return response.data
    },

    getConfig: async (partnerSlug: string): Promise<PartnerConfig> => {
      const response = await api.get(`/partner-admin/${partnerSlug}/config`)
      return response.data
    }
  },

  // Dashboard
  dashboard: {
    get: async (): Promise<DashboardData> => {
      const response = await api.get('/partner-admin/dashboard')
      return response.data
    },

    getProfile: async (): Promise<PartnerProfile> => {
      const response = await api.get('/partner-admin/profile')
      return response.data
    },

    updateProfile: async (data: { name?: string; contactEmail?: string; contactPhone?: string }): Promise<PartnerProfile> => {
      const response = await api.put('/partner-admin/profile', data)
      return response.data
    },

    healthCheck: async (): Promise<{ status: string; message: string; partnerId: string; timestamp: string }> => {
      const response = await api.get('/partner-admin/health')
      return response.data
    },

    getNotificationSettings: async (): Promise<NotificationSettings> => {
      const response = await api.get('/partner-admin/notification-settings')
      return response.data
    },

    updateNotificationSettings: async (data: Partial<NotificationSettings>): Promise<NotificationSettings> => {
      const response = await api.put('/partner-admin/notification-settings', data)
      return response.data
    }
  },

  // Security
  security: {
    changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
      const response = await api.post('/partner-admin/security/change-password', {
        currentPassword,
        newPassword
      })
      return response.data
    },

    getMfaStatus: async (): Promise<{ mfa_enabled: boolean }> => {
      const response = await api.get('/partner-admin/security/mfa-status')
      return response.data
    },

    setupMfa: async (): Promise<MfaSetupResponse> => {
      const partnerSlug = getPartnerSlug()
      const response = await api.post(`/partner-admin/${partnerSlug}/auth/mfa/setup`)
      return response.data
    },

    confirmMfa: async (code: string): Promise<{ success: boolean; message: string }> => {
      const partnerSlug = getPartnerSlug()
      const response = await api.post(`/partner-admin/${partnerSlug}/auth/mfa/confirm`, { code })
      return response.data
    },

    disableMfa: async (code: string): Promise<{ success: boolean; message: string }> => {
      const partnerSlug = getPartnerSlug()
      const response = await api.post(`/partner-admin/${partnerSlug}/auth/mfa/disable`, { code })
      return response.data
    },

    getSessions: async (): Promise<{
      currentSession: { ip: string; userAgent: string; loginTime: string }
      currentSessionId: string | null
      lastLogin: string | null
      otherSessions: Array<{
        id: string
        ip: string | null
        userAgent: string | null
        createdAt: string
        lastSeen: string | null
      }>
    }> => {
      const response = await api.get('/partner-admin/security/sessions')
      return response.data
    },

    terminateOtherSessions: async (): Promise<{ access_token: string; refresh_token: string }> => {
      const response = await api.post('/partner-admin/security/sessions/terminate-others')
      return response.data
    },

    revokeSession: async (sessionId: string): Promise<{ success: boolean; message: string }> => {
      const response = await api.post('/partner-admin/security/sessions/revoke', { sessionId })
      return response.data
    }
  },

  // Clients (Tenants/Medical Practices)
  clients: {
    list: async (params?: {
      page?: number
      limit?: number
      search?: string
      status?: string
    }): Promise<ClientsResponse> => {
      const response = await api.get('/partner-admin/clients', { params })
      return response.data
    },

    get: async (clientId: string): Promise<ClientDetails> => {
      const response = await api.get(`/partner-admin/clients/${clientId}`)
      return response.data
    },

    create: async (clientData: {
      name: string
      email: string
      phone?: string
      cpf?: string
      cnpj?: string
      speciality?: string
      subscriptionPlan?: string
      location?: string
    }): Promise<any> => {
      const response = await api.post('/partner-admin/clients', clientData)
      return response.data
    },

    updateStatus: async (clientId: string, status: string): Promise<any> => {
      const response = await api.put(`/partner-admin/clients/${clientId}/status`, { status })
      return response.data
    },

    update: async (clientId: string, updateData: {
      name?: string
      company_name?: string
      cnpj?: string
      email?: string
      phone?: string
      speciality?: string
      location?: string
      subscription_status?: 'trial' | 'active' | 'suspended' | 'cancelled'
      subscriptionPlan?: string
      custom_domain?: string
      logo_url?: string
      primary_color?: string
      secondary_color?: string
      settings?: Record<string, any>
      admin_notes?: string
    }): Promise<any> => {
      const response = await api.patch(`/partner-admin/clients/${clientId}`, updateData)
      return response.data
    },

    cancel: async (clientId: string, data: {
      reason: string
      details?: string
    }): Promise<any> => {
      const response = await api.post(`/partner-admin/clients/${clientId}/cancel`, data)
      return response.data
    },

    reactivate: async (clientId: string): Promise<any> => {
      const response = await api.post(`/partner-admin/clients/${clientId}/reactivate`)
      return response.data
    },

    getPerformance: async (clientId: string, period: '7d' | '30d' | '90d' = '30d'): Promise<any> => {
      const response = await api.get(`/partner-admin/clients/${clientId}/performance`, {
        params: { period }
      })
      return response.data
    },

    getMyClients: async (): Promise<any[]> => {
      const response = await api.get('/partner-admin/clients/accountant/list')
      return response.data
    },

    // Assignment endpoints
    assign: async (clientId: string, accountantId: string, notes?: string): Promise<any> => {
      const response = await api.post(`/partner-admin/clients/${clientId}/assign`, {
        accountantId,
        notes
      })
      return response.data
    },

    unassign: async (clientId: string, accountantId: string): Promise<any> => {
      const response = await api.delete(`/partner-admin/clients/${clientId}/assign/${accountantId}`)
      return response.data
    },

    bulkAssign: async (accountantId: string, clientIds: string[]): Promise<any> => {
      const response = await api.post('/partner-admin/clients/bulk-assign', {
        accountantId,
        clientIds
      })
      return response.data
    },

    // CNPJ Validation endpoints
    cnpj: {
      validate: async (cnpj: string): Promise<CNPJValidationResult> => {
        const response = await api.get(`/partner-admin/clients/cnpj/validate/${cnpj}`)
        return response.data
      },

      lookup: async (cnpj: string): Promise<CNPJLookupResult> => {
        const response = await api.get(`/partner-admin/clients/cnpj/lookup/${cnpj}`)
        return response.data
      },

      validateFull: async (cnpj: string): Promise<CNPJFullValidationResult> => {
        const response = await api.post('/partner-admin/clients/cnpj/validate-full', { cnpj })
        return response.data
      },

      checkExists: async (cnpj: string): Promise<CNPJExistsResult> => {
        const response = await api.get(`/partner-admin/clients/cnpj/check-exists/${cnpj}`)
        return response.data
      }
    }
  },

  // Accountants management
  accountants: {
    list: async (): Promise<{ accountants: Accountant[]; total: number }> => {
      const response = await api.get('/partner-admin/accountants')
      return response.data
    },

    getClients: async (accountantId: string): Promise<any[]> => {
      const response = await api.get(`/partner-admin/accountants/${accountantId}/clients`)
      return response.data
    },

    getAllAssignments: async (): Promise<{ assignments: Assignment[]; total: number }> => {
      const response = await api.get('/partner-admin/accountants/assignments/all')
      return response.data
    },

    getWorkloadAlerts: async (params?: {
      warningThreshold?: number
      criticalThreshold?: number
    }): Promise<{
      alerts: {
        type: 'critical' | 'warning' | 'info'
        accountantId: string
        accountantName: string
        accountantEmail: string
        clientCount: number
        threshold: number
        message: string
        recommendation: string
      }[]
      summary: {
        totalAccountants: number
        totalClients: number
        avgClientsPerAccountant: number
        overloadedCount: number
        underutilizedCount: number
        criticalCount: number
        warningCount: number
      }
      thresholds: {
        warning: number
        critical: number
      }
      accountantsBreakdown: {
        id: string
        name: string
        clientCount: number
        status: 'critical' | 'warning' | 'underutilized' | 'normal'
        capacityUsed: number
      }[]
      generatedAt: string
    }> => {
      const response = await api.get('/partner-admin/accountants/workload/alerts', { params })
      return response.data
    }
  },

  // Tax Management
  tax: {
    getOverview: async (params?: { month?: number; year?: number }): Promise<TaxOverview> => {
      const response = await api.get('/partner-admin/tax/overview', { params })
      return response.data
    },

    getCalendar: async (params?: {
      month?: string | number
      year?: number
      status?: string
      clientId?: string
    }): Promise<TaxCalendarResponse> => {
      const response = await api.get('/partner-admin/tax/calendar', { params })
      return response.data
    },

    getClientTaxDetails: async (clientId: string): Promise<ClientTaxDetails> => {
      const response = await api.get(`/partner-admin/tax/clients/${clientId}`)
      return response.data
    },

    bulkCalculate: async (calculations: {
      clientId: string
      type: 'DAS' | 'IRPF' | 'ISS'
      revenue: number
      period: string
    }[]): Promise<{ results: TaxCalculationResult[] }> => {
      const response = await api.post('/partner-admin/tax/calculate/bulk', { calculations })
      return response.data
    },

    getComplianceReport: async (params?: {
      startDate?: string
      endDate?: string
    }): Promise<ComplianceReport> => {
      const response = await api.get('/partner-admin/tax/reports/compliance', { params })
      return response.data
    },

    setupAlerts: async (config: {
      daysBeforeDue: number[]
      notifyEmail: boolean
      notifySystem: boolean
    }): Promise<{ success: boolean; message: string }> => {
      const response = await api.post('/partner-admin/tax/alerts/setup', config)
      return response.data
    },

    processPayment: async (
      clientId: string,
      obligationId: string,
      paymentData: {
        amount: number
        paidAt: string
        paymentMethod: string
        notes?: string
      }
    ): Promise<{ success: boolean; obligation: TaxObligation }> => {
      const response = await api.post(
        `/partner-admin/tax/clients/${clientId}/obligations/${obligationId}/pay`,
        paymentData
      )
      return response.data
    }
  },

  // Document Management
  documents: {
    list: async (params?: {
      page?: number
      limit?: number
      type?: string
      status?: string
      clientId?: string
      month?: number
      year?: number
    }): Promise<DocumentsResponse> => {
      const response = await api.get('/partner-admin/documents', { params })
      return response.data
    },

    getProcessingStatus: async (params?: { month?: number; year?: number }): Promise<ProcessingStatus> => {
      const response = await api.get('/partner-admin/documents/processing-status', { params })
      return response.data
    },

    bulkUpload: async (files: {
      name: string
      clientId: string
      type: string
      content?: string
    }[]): Promise<{
      uploadId: string
      results: {
        fileName: string
        clientId: string
        status: 'success' | 'failed'
        processingId: string
        estimatedCompletion: string
      }[]
      summary: {
        total: number
        successful: number
        failed: number
      }
      message: string
    }> => {
      const response = await api.post('/partner-admin/documents/bulk-upload', { files })
      return response.data
    },

    getCompliance: async (period?: string): Promise<any> => {
      const response = await api.get('/partner-admin/documents/compliance', {
        params: { period }
      })
      return response.data
    },

    getTemplates: async (category?: string): Promise<TemplatesResponse> => {
      const response = await api.get('/partner-admin/documents/templates', {
        params: { category }
      })
      return response.data
    },

    getAnalytics: async (timeRange?: string): Promise<DocumentAnalytics> => {
      const response = await api.get('/partner-admin/documents/analytics', {
        params: { timeRange }
      })
      return response.data
    }
  },

  // Support N1
  support: {
    // Tickets
    getTickets: async (params?: {
      page?: number
      limit?: number
      status?: SupportTicketStatus
      priority?: SupportTicketPriority
    }): Promise<SupportTicketsResponse> => {
      const response = await api.get('/partner-admin/support/tickets', { params })
      return response.data
    },

    getTicketStats: async (): Promise<{
      summary: SupportTicketsResponse['summary']
      pagination: SupportTicketsResponse['pagination']
    }> => {
      const response = await api.get('/partner-admin/support/tickets/stats')
      return response.data
    },

    getTicketDetails: async (ticketId: string): Promise<SupportTicketDetails> => {
      const response = await api.get(`/partner-admin/support/tickets/${ticketId}`)
      return response.data
    },

    createTicket: async (ticketData: {
      title: string
      description: string
      clientId: string
      category?: SupportTicketCategory
      priority?: SupportTicketPriority
    }): Promise<{
      ticket: any
      message: string
      nextSteps: string[]
    }> => {
      const response = await api.post('/partner-admin/support/tickets', ticketData)
      return response.data
    },

    replyTicket: async (ticketId: string, replyData: {
      message: string
      isInternal?: boolean
      attachments?: string[]
    }): Promise<{
      ticketId: string
      replyId: string
      message: string
      isInternal: boolean
      author: string
      createdAt: string
      attachments: string[]
    }> => {
      const response = await api.post(`/partner-admin/support/tickets/${ticketId}/reply`, replyData)
      return response.data
    },

    updateTicketStatus: async (ticketId: string, statusData: {
      status: SupportTicketStatus
      notes?: string
    }): Promise<{
      ticketId: string
      previousStatus: string
      newStatus: string
      updatedBy: string
      updatedAt: string
      notes?: string
      message: string
    }> => {
      const response = await api.put(`/partner-admin/support/tickets/${ticketId}/status`, statusData)
      return response.data
    },

    escalateTicket: async (ticketId: string, escalationData: {
      reason: string
      priority?: 'high' | 'urgent'
    }): Promise<{
      ticketId: string
      escalationId: string
      escalatedTo: string
      reason: string
      escalatedBy: string
      escalatedAt: string
      expectedResponse: string
      message: string
    }> => {
      const response = await api.post(`/partner-admin/support/tickets/${ticketId}/escalate`, escalationData)
      return response.data
    },

    // Metrics
    getMetrics: async (period: '7d' | '30d' | '90d' = '30d'): Promise<SupportMetrics> => {
      const response = await api.get('/partner-admin/support/metrics', { params: { period } })
      return response.data
    },

    // Knowledge Base
    getKnowledgeBase: async (params?: {
      category?: string
      search?: string
    }): Promise<KnowledgeBaseResponse> => {
      const response = await api.get('/partner-admin/support/knowledge-base', { params })
      return response.data
    },

    getArticle: async (articleId: string): Promise<KnowledgeBaseArticle & {
      content: string
      relatedArticles: KnowledgeBaseArticle[]
    }> => {
      const response = await api.get(`/partner-admin/support/knowledge-base/${articleId}`)
      return response.data
    },

    // Quick Actions & Status
    getQuickActions: async (): Promise<{ actions: QuickAction[] }> => {
      const response = await api.get('/partner-admin/support/quick-actions')
      return response.data
    },

    getSystemStatus: async (): Promise<SystemStatus> => {
      const response = await api.get('/partner-admin/support/system-status')
      return response.data
    }
  },

  // Analytics
  analytics: {
    // Overview
    getOverview: async (period: '7d' | '30d' | '90d' | '12m' = '30d'): Promise<AnalyticsOverview> => {
      const response = await api.get('/partner-admin/analytics/overview', { params: { period } })
      return response.data
    },

    // Financial Analytics
    getFinancial: async (params?: {
      startDate?: string
      endDate?: string
    }): Promise<FinancialAnalytics> => {
      const response = await api.get('/partner-admin/analytics/financial', { params })
      return response.data
    },

    // Revenue Analysis
    getRevenue: async (params?: {
      period?: string
      groupBy?: 'client' | 'service' | 'month'
    }): Promise<RevenueAnalysis> => {
      const response = await api.get('/partner-admin/analytics/revenue', { params })
      return response.data
    },

    // Operational Metrics
    getOperational: async (period: string = '30d'): Promise<OperationalMetrics> => {
      const response = await api.get('/partner-admin/analytics/operational', { params: { period } })
      return response.data
    },

    // Client Performance
    getClientPerformance: async (params?: {
      period?: string
      sortBy?: 'revenue' | 'activity' | 'healthScore'
      limit?: number
    }): Promise<ClientPerformance> => {
      const response = await api.get('/partner-admin/analytics/clients', { params })
      return response.data
    },

    // Compare Clients
    compareClients: async (data: {
      clientIds: string[]
      metrics?: string[]
    }): Promise<ClientComparison> => {
      const response = await api.post('/partner-admin/analytics/clients/compare', data)
      return response.data
    },

    // Growth Analytics
    getGrowth: async (period: '6m' | '12m' | '24m' = '12m'): Promise<GrowthAnalytics> => {
      const response = await api.get('/partner-admin/analytics/growth', { params: { period } })
      return response.data
    },

    // Churn Analytics
    getChurn: async (period: string = '90d'): Promise<ChurnAnalytics> => {
      const response = await api.get('/partner-admin/analytics/churn', { params: { period } })
      return response.data
    },

    // LTV Analytics
    getLTV: async (): Promise<LTVAnalytics> => {
      const response = await api.get('/partner-admin/analytics/ltv')
      return response.data
    },

    // Report Templates
    getTemplates: async (): Promise<ReportTemplatesResponse> => {
      const response = await api.get('/partner-admin/analytics/templates')
      return response.data
    },

    // Generate Custom Report
    generateCustomReport: async (config: {
      type: string
      dateRange?: { start: string; end: string }
      clients?: string[]
      metrics?: string[]
      groupBy?: string
      filters?: Record<string, any>
    }): Promise<any> => {
      const response = await api.post('/partner-admin/analytics/custom', config)
      return response.data
    },

    // Export Report
    exportReport: async (reportId: string, format: 'pdf' | 'excel' | 'csv' = 'pdf'): Promise<ExportResult> => {
      const response = await api.get(`/partner-admin/analytics/export/${reportId}`, { params: { format } })
      return response.data
    }
  }
}

// Storage utilities for auth
export const storageUtils = {
  setAuthData: (token: string, user: PartnerUser, partner: Partner, refreshToken?: string) => {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    localStorage.setItem(PARTNER_KEY, JSON.stringify(partner))
    localStorage.setItem('ngc_partner-admin_is_authenticated', 'true')
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    }
  },

  clearAuthData,

  getAuthToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: (): PartnerUser | null => {
    try {
      const data = localStorage.getItem(USER_KEY)
      return data ? JSON.parse(data) : null
    } catch {
      return null
    }
  },
  getPartner: (): Partner | null => {
    try {
      const data = localStorage.getItem(PARTNER_KEY)
      return data ? JSON.parse(data) : null
    } catch {
      return null
    }
  },
  isAuthenticated: () => localStorage.getItem('ngc_partner-admin_is_authenticated') === 'true'
}

export { api as partnerAdminClient }

// =====================================================
// TEAM MANAGEMENT TYPES AND API
// =====================================================

export type TeamMemberRole = 'admin' | 'accountant' | 'assistant'
export type TeamMemberStatus = 'active' | 'inactive' | 'pending_invitation'

export interface TeamMemberPermissions {
  clients: { view: boolean; create: boolean; edit: boolean; delete: boolean; assign: boolean }
  documents: { view: boolean; upload: boolean; approve: boolean; delete: boolean }
  tax: { view: boolean; calculate: boolean; submit: boolean }
  support: { view: boolean; respond: boolean; escalate: boolean }
  reports: { view: boolean; export: boolean }
  team: { view: boolean; manage: boolean }
  settings: { view: boolean; edit: boolean }
}

export interface TeamMemberMetrics {
  clientsManaged: number
  documentsProcessed: number
  ticketsResolved: number
  tasksCompleted: number
  avgResponseTime: number
  satisfactionScore: number
}

export interface TeamMember {
  id: string
  name: string
  email: string
  phone?: string
  role: TeamMemberRole
  department: string
  status: TeamMemberStatus
  permissions: TeamMemberPermissions
  metrics: TeamMemberMetrics
  avatar?: string
  lastLogin?: string
  invitedAt?: string
  createdAt: string
  updatedAt: string
  mfaEnabled: boolean
}

export interface TeamMembersResponse {
  members: TeamMember[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface TeamMetrics {
  overview: {
    totalMembers: number
    activeMembers: number
    avgSatisfactionScore: number
    avgResponseTime: number
    totalTasksCompleted: number
    totalTasksOverdue: number
  }
  topPerformers: {
    id: string
    name: string
    role: TeamMemberRole
    score: number
    tasksCompleted: number
  }[]
  workloadDistribution: {
    id: string
    name: string
    clientsAssigned: number
    documentsProcessed: number
    workloadScore: number
  }[]
  activityTimeline: {
    date: string
    documentsProcessed: number
    ticketsResolved: number
    tasksCompleted: number
  }[]
}

export interface RoleDefinition {
  id: TeamMemberRole
  name: string
  description: string
  defaultPermissions: TeamMemberPermissions
}

export interface DepartmentDefinition {
  id: string
  name: string
  description: string
}

// Team API endpoints
export const teamApi = {
  // List team members
  getMembers: async (params?: {
    page?: number
    limit?: number
    role?: TeamMemberRole
    status?: TeamMemberStatus
    search?: string
  }): Promise<TeamMembersResponse> => {
    const response = await api.get('/partner-admin/team', { params })
    return response.data
  },

  // Get team metrics
  getMetrics: async (): Promise<TeamMetrics> => {
    const response = await api.get('/partner-admin/team/metrics')
    return response.data
  },

  // Get available roles
  getRoles: async (): Promise<RoleDefinition[]> => {
    const response = await api.get('/partner-admin/team/roles')
    return response.data
  },

  // Get departments
  getDepartments: async (): Promise<DepartmentDefinition[]> => {
    const response = await api.get('/partner-admin/team/departments')
    return response.data
  },

  // Get member by ID
  getMember: async (memberId: string): Promise<TeamMember> => {
    const response = await api.get(`/partner-admin/team/${memberId}`)
    return response.data
  },

  // Create new member
  createMember: async (data: {
    name: string
    email: string
    phone?: string
    role: TeamMemberRole
    department: string
    permissions?: Partial<TeamMemberPermissions>
  }): Promise<{ member: TeamMember; message: string }> => {
    const response = await api.post('/partner-admin/team', data)
    return response.data
  },

  // Update member
  updateMember: async (memberId: string, data: Partial<TeamMember>): Promise<TeamMember> => {
    const response = await api.put(`/partner-admin/team/${memberId}`, data)
    return response.data
  },

  // Delete member
  deleteMember: async (memberId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/partner-admin/team/${memberId}`)
    return response.data
  },

  // Resend invitation
  resendInvitation: async (memberId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/partner-admin/team/${memberId}/resend-invitation`)
    return response.data
  },

  // Toggle MFA
  toggleMfa: async (memberId: string, enabled: boolean): Promise<{ success: boolean; mfaEnabled: boolean }> => {
    const response = await api.post(`/partner-admin/team/${memberId}/mfa`, { enabled })
    return response.data
  },

  // Update permissions
  updatePermissions: async (memberId: string, permissions: TeamMemberPermissions): Promise<TeamMember> => {
    const response = await api.put(`/partner-admin/team/${memberId}/permissions`, { permissions })
    return response.data
  },

  // Activate member
  activateMember: async (memberId: string): Promise<TeamMember> => {
    const response = await api.post(`/partner-admin/team/${memberId}/activate`)
    return response.data
  },

  // Deactivate member
  deactivateMember: async (memberId: string): Promise<TeamMember> => {
    const response = await api.post(`/partner-admin/team/${memberId}/deactivate`)
    return response.data
  }
}

// =====================================================
// WHITELABEL TYPES AND API
// =====================================================

export interface BrandingLogo {
  primary: string | null
  secondary: string | null
  favicon: string | null
  emailHeader: string | null
}

export interface BrandingColors {
  primary: string
  secondary: string
  accent: string
  background: string
  surface: string
  textPrimary: string
  textSecondary: string
  success: string
  warning: string
  error: string
  info: string
}

export interface BrandingTypography {
  fontFamily: string
  fontFamilySecondary: string
  fontSize: string
  fontWeightNormal: number
  fontWeightBold: number
  lineHeight: number
}

export interface BrandingDomain {
  subdomain: string
  customDomain: string | null
  sslEnabled: boolean
  sslExpiry: string | null
}

export interface BrandingContent {
  brandName: string
  tagline: string
  loginMessage: string
  footerText: string
  copyrightText: string
  showPoweredBy: boolean
}

export interface BrandingContact {
  email: string
  phone: string
  whatsapp: string
  address: string
}

export interface BrandingSocial {
  website: string
  facebook: string
  instagram: string
  linkedin: string
  twitter: string
  youtube: string
}

export interface BrandingEmail {
  fromName: string
  fromAddress: string
  replyTo: string
  signature: string
  headerColor: string
  footerText: string
}

export interface BrandingSeo {
  metaTitle: string
  metaDescription: string
  metaKeywords: string
  ogImage: string
}

export interface BrandingTheme {
  mode: 'light' | 'dark' | 'auto'
  sidebarStyle: 'default' | 'compact' | 'minimal'
  headerStyle: 'default' | 'transparent' | 'colored'
  borderRadius: 'none' | 'small' | 'medium' | 'large'
}

export interface BrandingCustomCss {
  global: string
  loginPage: string
  dashboard: string
  sidebar: string
  header: string
}

export interface BrandingFeatures {
  bankingIntegration: boolean
  documentManagement: boolean
  analyticsDashboard: boolean
  multiCompany: boolean
  apiAccess: boolean
  whiteLabel: boolean
  customEmails: boolean
  customReports: boolean
}

export interface PartnerBrandingConfig {
  id: string
  partnerId: string
  partnerName: string
  slug: string
  logo: BrandingLogo
  colors: BrandingColors
  typography: BrandingTypography
  domain: BrandingDomain
  content: BrandingContent
  contact: BrandingContact
  social: BrandingSocial
  email: BrandingEmail
  seo: BrandingSeo
  theme: BrandingTheme
  customCss: BrandingCustomCss
  features: BrandingFeatures
  createdAt: string
  updatedAt: string
  lastPublished: string | null
  publishedVersion: number
  draftVersion: number
  status: 'draft' | 'published' | 'pending_review'
}

export interface BrandingTemplate {
  id: string
  name: string
  description: string
  thumbnail: string
  category: 'medical' | 'accounting' | 'modern' | 'classic' | 'minimal'
  colors: BrandingColors
  typography: BrandingTypography
  theme: BrandingTheme
}

export interface BrandingPreview {
  previewUrl: string
  expiresAt: string
  previewId: string
}

export interface DomainVerification {
  domain: string
  verified: boolean
  verificationMethod: 'dns' | 'file'
  verificationToken: string
  instructions: string[]
  lastChecked: string
}

export interface BrandingHistory {
  version: number
  publishedAt: string
  publishedBy: string
  changes: string[]
}

// Whitelabel API endpoints
export const whitelabelApi = {
  // Get current branding configuration
  getBranding: async (): Promise<PartnerBrandingConfig> => {
    const response = await api.get('/partner-admin/whitelabel/branding')
    return response.data
  },

  // Update branding configuration
  updateBranding: async (updates: Partial<PartnerBrandingConfig>): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/branding', updates)
    return response.data
  },

  // Publish branding changes
  publishBranding: async (): Promise<{
    success: boolean
    message: string
    publishedAt: string
    version: number
  }> => {
    const response = await api.post('/partner-admin/whitelabel/branding/publish')
    return response.data
  },

  // Generate preview URL
  generatePreview: async (): Promise<BrandingPreview> => {
    const response = await api.post('/partner-admin/whitelabel/branding/preview')
    return response.data
  },

  // Reset branding to default
  resetToDefault: async (): Promise<PartnerBrandingConfig> => {
    const response = await api.post('/partner-admin/whitelabel/branding/reset')
    return response.data
  },

  // Get branding history
  getHistory: async (): Promise<BrandingHistory[]> => {
    const response = await api.get('/partner-admin/whitelabel/branding/history')
    return response.data
  },

  // Get theme CSS
  getThemeCSS: async (): Promise<string> => {
    const response = await api.get('/partner-admin/whitelabel/branding/css', {
      responseType: 'text'
    })
    return response.data
  },

  // Get available templates
  getTemplates: async (): Promise<BrandingTemplate[]> => {
    const response = await api.get('/partner-admin/whitelabel/templates')
    return response.data
  },

  // Apply a template
  applyTemplate: async (templateId: string): Promise<PartnerBrandingConfig> => {
    const response = await api.post(`/partner-admin/whitelabel/templates/${templateId}/apply`)
    return response.data
  },

  // Verify custom domain
  verifyDomain: async (domain: string): Promise<DomainVerification> => {
    const response = await api.post('/partner-admin/whitelabel/domain/verify', { domain })
    return response.data
  },

  // Request SSL certificate
  requestSSL: async (domain: string): Promise<{
    success: boolean
    message: string
    estimatedTime: string
  }> => {
    const response = await api.post('/partner-admin/whitelabel/domain/ssl', { domain })
    return response.data
  },

  // Update specific sections
  updateColors: async (colors: BrandingColors): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/colors', colors)
    return response.data
  },

  updateTypography: async (typography: BrandingTypography): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/typography', typography)
    return response.data
  },

  updateLogo: async (logo: BrandingLogo): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/logo', logo)
    return response.data
  },

  updateContent: async (content: BrandingContent): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/content', content)
    return response.data
  },

  updateContact: async (contact: BrandingContact): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/contact', contact)
    return response.data
  },

  updateSocial: async (social: BrandingSocial): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/social', social)
    return response.data
  },

  updateEmail: async (email: BrandingEmail): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/email', email)
    return response.data
  },

  updateSeo: async (seo: BrandingSeo): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/seo', seo)
    return response.data
  },

  updateTheme: async (theme: BrandingTheme): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/theme', theme)
    return response.data
  },

  updateCustomCss: async (customCss: BrandingCustomCss): Promise<PartnerBrandingConfig> => {
    const response = await api.put('/partner-admin/whitelabel/custom-css', customCss)
    return response.data
  }
}

// ==========================================
// CLIENT TAX SETTINGS TYPES AND API
// ==========================================

export type TaxRegime = 'mei' | 'simples' | 'presumido' | 'real'

export interface SimplesNacionalSettings {
  annex: 'III' | 'V'
  currentBand?: number
  rbt12?: number
  factorR?: number
  effectiveRate?: number
  deductionParcel?: number
  useFactorROptimization: boolean
  cpom?: string[]
}

export interface LucroPresumidoSettings {
  presumptionBase: number
  irpjRate: number
  csllRate: number
  pisRate: number
  cofinsRate: number
  additionalIR: boolean
  additionalIRThreshold: number
}

export interface ISSSettings {
  municipalityCode: string
  municipalityName: string
  aliquot: number
  incentiveAliquot?: number
  hasIncentive: boolean
  incentiveEndDate?: string
  nfseEnabled: boolean
  specialRegime?: string
}

export interface IRPFSettings {
  carneLeoEnabled: boolean
  bookCashEnabled: boolean
  deductibleExpenses: string[]
  dependentsCount: number
  hasOtherIncome: boolean
}

export interface WithholdingsSettings {
  irrf: {
    enabled: boolean
    threshold: number
    rate: number
  }
  pis: {
    enabled: boolean
    rate: number
  }
  cofins: {
    enabled: boolean
    rate: number
  }
  csll: {
    enabled: boolean
    rate: number
  }
  inss: {
    enabled: boolean
    rate: number
    ceiling: number
  }
  iss: {
    enabled: boolean
    rate: number
  }
}

export interface TaxNotificationPreferences {
  obligationReminder: boolean
  obligationReminderDays: number
  paymentReminder: boolean
  paymentReminderDays: number
  rateChanges: boolean
  legislationUpdates: boolean
  emailNotifications: boolean
  smsNotifications: boolean
  pushNotifications: boolean
}

export interface ClientTaxSettings {
  clientId: string
  clientName?: string
  taxRegime: TaxRegime
  cnpj?: string
  stateRegistration?: string
  municipalRegistration?: string
  crm?: string
  simplesNacional?: SimplesNacionalSettings
  lucroPresumido?: LucroPresumidoSettings
  iss?: ISSSettings
  irpf?: IRPFSettings
  withholdings?: WithholdingsSettings
  accessoryObligations?: string[]
  notificationPreferences?: TaxNotificationPreferences
  internalNotes?: string
  updatedAt?: string
  updatedBy?: string
}

export interface TaxRegimeInfo {
  code: TaxRegime
  name: string
  description: string
  revenueLimit?: string
  advantages: string[]
  disadvantages: string[]
  suitableFor: string[]
}

export interface AccessoryObligation {
  code: string
  name: string
  description: string
  frequency: 'monthly' | 'quarterly' | 'annual' | 'event'
  applicableRegimes: TaxRegime[]
  deadline: string
  system: string
  mandatory: boolean
}

export interface ISSRate {
  municipalityCode: string
  municipalityName: string
  uf: string
  standardRate: number
  medicalRate: number
  hasIncentive: boolean
  incentiveDescription?: string
}

export interface SimplesNacionalAnnex {
  annex: 'III' | 'V'
  description: string
  factorRRequired: boolean
  bands: Array<{
    band: number
    revenueFrom: number
    revenueTo: number
    nominalRate: number
    deductionParcel: number
  }>
}

export interface SimplesNacionalCalculation {
  annex: 'III' | 'V'
  rbt12: number
  factorR?: number
  band: number
  nominalRate: number
  effectiveRate: number
  deductionParcel: number
  taxAmount: number
  recommendation?: string
}

export interface TaxSettingsHistoryEntry {
  id: string
  clientId: string
  changedAt: string
  changedBy: string
  changedByName?: string
  changeType: 'created' | 'updated' | 'regime_change'
  previousValues?: Partial<ClientTaxSettings>
  newValues: Partial<ClientTaxSettings>
  description: string
}

// Client Tax Settings API endpoints
export const clientTaxSettingsApi = {
  // Get client tax settings
  getSettings: async (clientId: string): Promise<ClientTaxSettings> => {
    const response = await api.get(`/partner-admin/tax/clients/${clientId}/settings`)
    const data = response.data
    // Flatten settings into root level for frontend interface
    return {
      clientId: data.clientId,
      clientName: data.clientName,
      taxRegime: data.settings?.taxRegime || 'simples',
      cnpj: data.settings?.cnpj || data.cnpj,
      stateRegistration: data.settings?.stateRegistration,
      municipalRegistration: data.settings?.municipalRegistration,
      crm: data.settings?.crm,
      simplesNacional: data.settings?.simplesNacional,
      lucroPresumido: data.settings?.lucroPresumido,
      iss: data.settings?.iss,
      irpf: data.settings?.irpf,
      withholdings: data.settings?.withholdings,
      accessoryObligations: data.settings?.accessoryObligations,
      notificationPreferences: data.settings?.notificationPreferences,
      internalNotes: data.settings?.internalNotes,
      updatedAt: data.lastUpdated || data.settings?.updatedAt,
      updatedBy: data.updatedBy || data.settings?.updatedBy
    }
  },

  // Update client tax settings
  updateSettings: async (clientId: string, settings: Partial<ClientTaxSettings>): Promise<ClientTaxSettings> => {
    const response = await api.put(`/partner-admin/tax/clients/${clientId}/settings`, settings)
    return response.data
  },

  // Get available tax regimes
  getTaxRegimes: async (): Promise<TaxRegimeInfo[]> => {
    const response = await api.get('/partner-admin/tax/regimes')
    const regimes = response.data.regimes || response.data
    // Map backend fields to frontend interface
    return regimes.map((r: any) => ({
      code: r.id || r.code,
      name: r.name,
      description: r.description,
      revenueLimit: r.maxRevenue ? `R$ ${r.maxRevenue.toLocaleString('pt-BR')}` : undefined,
      advantages: r.benefits || r.advantages || [],
      disadvantages: r.restrictions || r.disadvantages || [],
      suitableFor: r.suitableFor || []
    }))
  },

  // Get accessory obligations
  getAccessoryObligations: async (): Promise<AccessoryObligation[]> => {
    const response = await api.get('/partner-admin/tax/accessory-obligations')
    const obligations = response.data.obligations || response.data
    // Map backend fields to frontend interface
    return obligations.map((o: any) => ({
      code: o.id || o.code,
      name: o.name,
      description: o.description || o.name,
      frequency: o.frequency,
      applicableRegimes: o.regime || o.applicableRegimes || [],
      deadline: o.deadline,
      system: o.system || '',
      mandatory: o.mandatory ?? true
    }))
  },

  // Get ISS rates
  getISSRates: async (uf?: string, search?: string): Promise<ISSRate[]> => {
    const params = new URLSearchParams()
    if (uf) params.append('uf', uf)
    if (search) params.append('search', search)
    const response = await api.get(`/partner-admin/tax/iss-rates?${params.toString()}`)
    return response.data
  },

  // Get Simples Nacional annexes
  getSimplesNacionalAnnexes: async (): Promise<SimplesNacionalAnnex[]> => {
    const response = await api.get('/partner-admin/tax/simples-nacional/annexes')
    return response.data.annexes || response.data
  },

  // Calculate Simples Nacional rate
  calculateSimplesNacionalRate: async (
    clientId: string,
    data: { rbt12: number; annex: 'III' | 'V'; factorR?: number }
  ): Promise<SimplesNacionalCalculation> => {
    const response = await api.post(`/partner-admin/tax/clients/${clientId}/simples-nacional/calculate`, data)
    const result = response.data
    // Map backend nested structure to frontend interface
    const calc = result.calculation || result
    return {
      annex: calc.annex,
      rbt12: calc.rbt12,
      factorR: calc.factorR,
      band: calc.band || 1,
      nominalRate: calc.nominalRate,
      effectiveRate: calc.effectiveRate,
      deductionParcel: calc.deduction || 0,
      taxAmount: calc.monthlyEstimate?.tax || 0,
      recommendation: result.recommendations?.[0]?.description || calc.recommendation
    }
  },

  // Get settings history
  getSettingsHistory: async (clientId: string, limit?: number): Promise<TaxSettingsHistoryEntry[]> => {
    const params = limit ? `?limit=${limit}` : ''
    const response = await api.get(`/partner-admin/tax/clients/${clientId}/settings/history${params}`)
    return response.data
  }
}
